import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
    adminAssignSubscriptionPlanToSupplier,
    adminDeleteSupplierSubscriptionPlan,
    adminGetSubscriptionRequests,
    adminGetSupplierSubscriptionPlanMatrix,
    adminReviewSubscriptionRequest,
} from "../../api/subscriptionApi";
import "../../Admin/AdminStyles.css";
import "./AdminSubscriptions.css";

const euro = (cents) => {
    const value = Number(cents);
    if (!Number.isFinite(value)) return "—";

    return (value / 100).toLocaleString("it-IT", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

function prettyJson(value) {
    try {
        return JSON.stringify(value || {}, null, 2);
    } catch {
        return "{}";
    }
}

function statusLabel(status) {
    if (status === "pending") return "In attesa";
    if (status === "approved") return "Approvato";
    if (status === "rejected") return "Rifiutato";
    return status || "—";
}

function getSupplierLocation(supplier) {
    const pieces = [supplier?.citta, supplier?.provincia].filter(Boolean);
    return pieces.length ? pieces.join(" · ") : "Località non indicata";
}

// Zone di lavoro (supplier_zones) del fornitore, non la residenza:
// usate dalla search "città" della Tabella 2 (dove lavora, non dove abita).
function getSupplierWorkZoneComuni(supplier) {
    const zones = Array.isArray(supplier?.zone_lavoro) ? supplier.zone_lavoro : [];
    return zones.map((z) => z?.comune).filter(Boolean);
}

function getPlanOptionLabel(plan) {
    if (!plan) return "Piano";
    const model = plan.model_code ? ` · ${plan.model_code}` : "";
    return `${plan.name || `#${plan.id}`}${model} · ${euro(plan.price_cents)}`;
}

function billingPeriodLabel(period) {
    if (period === "monthly") return "/ mese";
    if (!period) return "";
    return `/ ${period}`;
}

function formatShortDate(value) {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("it-IT");
}

function isManualAdminAssociation(item) {
    const note = String(item?.review_note || "").toLowerCase();
    return note.includes("associazione manuale admin");
}

function requestOriginLabel(item) {
    if (isManualAdminAssociation(item)) return "Admin";
    return "Fornitore";
}

function getRequestActionText(item) {
    if (isManualAdminAssociation(item)) return "Associazione admin";
    if (item?.status === "approved") return "Già approvata";
    if (item?.status === "rejected") return "Rifiutata";
    return "—";
}

/* ---------------- Sort (client-side, per le 2 tabelle della pagina) ---------------- */

// Ordine di attenzione per l'admin: le richieste da gestire vengono prima.
const STATUS_SORT_RANK = { pending: 0, approved: 1, rejected: 2 };

function isEmptyValue(v) {
    return v === null || v === undefined || v === "";
}

function compareByType(a, b, type) {
    if (type === "number") return Number(a) - Number(b);
    if (type === "date") return new Date(a).getTime() - new Date(b).getTime();
    return String(a).localeCompare(String(b), "it", {
        sensitivity: "base",
        numeric: true,
    });
}

// null/vuoti finiscono sempre in fondo, indipendentemente da asc/desc.
function sortRows(rows, sortState, columns) {
    const { key, dir } = sortState || {};
    const column = key ? columns[key] : null;
    if (!column) return rows;

    const dirMul = dir === "desc" ? -1 : 1;

    return [...rows].sort((rowA, rowB) => {
        const a = column.getValue(rowA);
        const b = column.getValue(rowB);
        const aEmpty = isEmptyValue(a);
        const bEmpty = isEmptyValue(b);

        if (aEmpty && bEmpty) return 0;
        if (aEmpty) return 1;
        if (bEmpty) return -1;

        return dirMul * compareByType(a, b, column.type);
    });
}

function toggleSort(setSortState, key) {
    setSortState((prev) =>
        prev.key === key
            ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
            : { key, dir: "asc" },
    );
}

function SortIndicator({ active, dir }) {
    if (!active) return null;
    return (
        <span className="admin-th-sort-indicator" aria-hidden="true">
            {dir === "desc" ? "▼" : "▲"}
        </span>
    );
}

function SortableTh({ label, sortKey, sortState, onSort, children }) {
    const active = sortState.key === sortKey;
    return (
        <th
            className={"admin-th-sortable" + (active ? " is-active" : "")}
            onClick={() => onSort(sortKey)}
            role="button"
            tabIndex={0}
            aria-sort={
                active ? (sortState.dir === "desc" ? "descending" : "ascending") : "none"
            }
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSort(sortKey);
                }
            }}
        >
            {label}
            <SortIndicator active={active} dir={sortState.dir} />
            {children}
        </th>
    );
}

// Tabella 1 "Richieste fornitori": colonne ordinabili (Azioni escluse, non ha dato).
const REQUESTS_SORT_COLUMNS = {
    id: {
        type: "number",
        getValue: (item) => item.id,
    },
    supplier: {
        type: "string",
        getValue: (item) =>
            item.supplier_name || (item.supplier_id != null ? `#${item.supplier_id}` : null),
    },
    plan: {
        type: "string",
        getValue: (item) => item.plan_name,
    },
    status: {
        type: "number",
        getValue: (item) => STATUS_SORT_RANK[item.status] ?? 99,
    },
    origin: {
        type: "string",
        getValue: (item) => requestOriginLabel(item),
    },
    review: {
        type: "date",
        getValue: (item) => item.reviewed_at,
    },
    overrides: {
        type: "number",
        getValue: (item) => Object.keys(item.custom_overrides || {}).length,
    },
};

// Tabella 2 "Associazioni fornitori · piani": "Associa nuovo piano" esclusa
// (colonna di sola azione, nessun dato della riga da ordinare).
const SUPPLIER_SORT_COLUMNS = {
    supplier: {
        type: "string",
        getValue: (s) => s.nome || (s.id != null ? `#${s.id}` : null),
    },
    location: {
        type: "string",
        getValue: (s) => [s.citta, s.provincia].filter(Boolean).join(" · ") || null,
    },
    plans: {
        type: "number",
        getValue: (s) => (Array.isArray(s.subscription_plans) ? s.subscription_plans.length : 0),
    },
};

export default function AdminSubscriptionsRequests() {
    const token = localStorage.getItem("token");

    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [statusFilter, setStatusFilter] = useState("");
    const [requestsSort, setRequestsSort] = useState({ key: null, dir: "asc" });

    const [supplierRows, setSupplierRows] = useState([]);
    const [planOptions, setPlanOptions] = useState([]);
    const [loadingSupplierPlans, setLoadingSupplierPlans] = useState(true);
    const [supplierPlanBusyId, setSupplierPlanBusyId] = useState(null);
    const [selectedPlanBySupplier, setSelectedPlanBySupplier] = useState({});
    const [supplierSearch, setSupplierSearch] = useState("");
    const [suppliersSort, setSuppliersSort] = useState({ key: null, dir: "asc" });

    const filteredLabel = useMemo(() => {
        if (!statusFilter) return "Tutte";
        if (statusFilter === "pending") return "In attesa";
        if (statusFilter === "approved") return "Approvate";
        if (statusFilter === "rejected") return "Rifiutate";
        return statusFilter;
    }, [statusFilter]);

    const sortedRequests = useMemo(
        () => sortRows(requests, requestsSort, REQUESTS_SORT_COLUMNS),
        [requests, requestsSort],
    );

    const filteredSupplierRows = useMemo(() => {
        const q = supplierSearch.trim().toLowerCase();
        if (!q) return supplierRows;

        return supplierRows.filter((supplier) => {
            const plansText = (supplier.subscription_plans || [])
                .map((link) => link.plan_name)
                .filter(Boolean)
                .join(" ");

            // "città" della search = zone di LAVORO (supplier_zones), non la
            // residenza del fornitore (fornitori.citta/provincia).
            const workZonesText = getSupplierWorkZoneComuni(supplier).join(" ");

            return [supplier.nome, workZonesText, plansText]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes(q);
        });
    }, [supplierRows, supplierSearch]);

    const sortedSupplierRows = useMemo(
        () => sortRows(filteredSupplierRows, suppliersSort, SUPPLIER_SORT_COLUMNS),
        [filteredSupplierRows, suppliersSort],
    );

    const supplierPlanStats = useMemo(() => {
        const stats = {
            totalSuppliers: supplierRows.length,
            suppliersWithPlans: 0,
            suppliersWithoutPlans: 0,
            totalLinks: 0,
            approvedLinks: 0,
            pendingLinks: 0,
            rejectedLinks: 0,
        };

        supplierRows.forEach((supplier) => {
            const links = Array.isArray(supplier.subscription_plans)
                ? supplier.subscription_plans
                : [];

            if (links.length > 0) stats.suppliersWithPlans += 1;

            links.forEach((link) => {
                stats.totalLinks += 1;
                if (link.status === "approved") stats.approvedLinks += 1;
                if (link.status === "pending") stats.pendingLinks += 1;
                if (link.status === "rejected") stats.rejectedLinks += 1;
            });
        });

        stats.suppliersWithoutPlans = Math.max(
            0,
            stats.totalSuppliers - stats.suppliersWithPlans,
        );

        return stats;
    }, [supplierRows]);

    function getFirstAvailablePlanId(supplier, plans = planOptions) {
        const linkedPlanIds = new Set(
            (supplier?.subscription_plans || []).map((link) =>
                String(link.plan_id),
            ),
        );

        const firstAvailable = (plans || []).find(
            (plan) => !linkedPlanIds.has(String(plan.id)),
        );

        return firstAvailable?.id ? String(firstAvailable.id) : "";
    }

    async function loadRequests() {
        if (!token) return;
        setLoading(true);
        try {
            const data = await adminGetSubscriptionRequests(token, {
                status: statusFilter || undefined,
            });
            setRequests(data?.requests || []);
        } catch (err) {
            toast.error(err.message || "Errore caricamento richieste");
        } finally {
            setLoading(false);
        }
    }

    async function loadSupplierPlans() {
        if (!token) return;
        setLoadingSupplierPlans(true);

        try {
            const data = await adminGetSupplierSubscriptionPlanMatrix(token);
            const nextSuppliers = Array.isArray(data?.suppliers)
                ? data.suppliers
                : [];
            const nextPlans = Array.isArray(data?.plans) ? data.plans : [];

            setSupplierRows(nextSuppliers);
            setPlanOptions(nextPlans);

            setSelectedPlanBySupplier((prev) => {
                const next = { ...prev };

                nextSuppliers.forEach((supplier) => {
                    const current = next[supplier.id];
                    const linkedPlanIds = new Set(
                        (supplier.subscription_plans || []).map((link) =>
                            String(link.plan_id),
                        ),
                    );

                    const currentStillAvailable =
                        current &&
                        nextPlans.some(
                            (plan) =>
                                String(plan.id) === String(current) &&
                                !linkedPlanIds.has(String(plan.id)),
                        );

                    if (!currentStillAvailable) {
                        next[supplier.id] = getFirstAvailablePlanId(
                            supplier,
                            nextPlans,
                        );
                    }
                });

                return next;
            });
        } catch (err) {
            toast.error(
                err.message || "Errore caricamento associazioni fornitori",
            );
        } finally {
            setLoadingSupplierPlans(false);
        }
    }

    useEffect(() => {
        loadRequests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    useEffect(() => {
        loadSupplierPlans();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function handleReview(requestId, nextStatus) {
        if (!token) return;

        const reviewNote = window.prompt(
            nextStatus === "approved"
                ? "Nota interna facoltativa per approvazione:"
                : "Motivo / nota interna rifiuto:",
        );

        setBusyId(requestId);
        try {
            await adminReviewSubscriptionRequest(
                requestId,
                {
                    status: nextStatus,
                    review_note: reviewNote || "",
                },
                token,
            );
            toast.success(
                nextStatus === "approved"
                    ? "Richiesta approvata"
                    : "Richiesta rifiutata",
            );
            await Promise.all([loadRequests(), loadSupplierPlans()]);
        } catch (err) {
            toast.error(err.message || "Errore revisione richiesta");
        } finally {
            setBusyId(null);
        }
    }

    async function handleAssignPlan(supplier, forcedPlanId = null) {
        if (!token) return;

        const planId = forcedPlanId || selectedPlanBySupplier[supplier.id];
        if (!planId) {
            toast.error("Seleziona un piano da associare");
            return;
        }

        const selectedPlan = planOptions.find(
            (plan) => String(plan.id) === String(planId),
        );

        const ok = window.confirm(
            `Associare il piano "${selectedPlan?.name || planId}" a "${
                supplier.nome || `#${supplier.id}`
            }"?`,
        );

        if (!ok) return;

        setSupplierPlanBusyId(`assign-${supplier.id}-${planId}`);

        try {
            await adminAssignSubscriptionPlanToSupplier(
                {
                    supplier_id: supplier.id,
                    plan_id: Number(planId),
                    status: "approved",
                    review_note: "Associazione manuale admin",
                },
                token,
            );

            toast.success("Piano associato al fornitore");
            await Promise.all([loadRequests(), loadSupplierPlans()]);
        } catch (err) {
            toast.error(err.message || "Errore associazione piano");
        } finally {
            setSupplierPlanBusyId(null);
        }
    }

    async function handleDeleteSupplierPlan(link) {
        if (!token) return;

        const ok = window.confirm(
            `Rimuovere l'associazione "${link.plan_name}" dal fornitore?`,
        );

        if (!ok) return;

        setSupplierPlanBusyId(`delete-${link.id}`);

        try {
            await adminDeleteSupplierSubscriptionPlan(link.id, token);
            toast.success("Associazione rimossa");
            await Promise.all([loadRequests(), loadSupplierPlans()]);
        } catch (err) {
            toast.error(err.message || "Errore rimozione associazione");
        } finally {
            setSupplierPlanBusyId(null);
        }
    }

    return (
        <div className="admin-page">
            <div className="admin-card">
                <div className="admin-card-header admin-card-header-inline">
                    <div>
                        <h1 className="admin-title">Abbonamenti · Richieste fornitori</h1>
                        <p className="admin-subtitle">
                            Gestisci le richieste di adesione dei fornitori ai piani configurati.
                        </p>
                    </div>

                    <div className="admin-inline-filters">
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="">Tutti gli stati</option>
                            <option value="pending">In attesa</option>
                            <option value="approved">Approvati</option>
                            <option value="rejected">Rifiutati</option>
                        </select>
                    </div>
                </div>

                <div className="admin-subtitle" style={{ marginBottom: 12 }}>
                    Filtro attivo: <strong>{filteredLabel}</strong>
                </div>

                {loading ? (
                    <div className="admin-empty-box">Caricamento richieste...</div>
                ) : requests.length === 0 ? (
                    <div className="admin-empty-box">Nessuna richiesta trovata.</div>
                ) : (
                    <div className="admin-table-wrap">
                        <table className="admin-table admin-requests-table admin-requests-table--compact">
                            <thead>
                                <tr>
                                    <SortableTh
                                        label="ID"
                                        sortKey="id"
                                        sortState={requestsSort}
                                        onSort={(key) => toggleSort(setRequestsSort, key)}
                                    />
                                    <SortableTh
                                        label="Fornitore"
                                        sortKey="supplier"
                                        sortState={requestsSort}
                                        onSort={(key) => toggleSort(setRequestsSort, key)}
                                    />
                                    <SortableTh
                                        label="Piano"
                                        sortKey="plan"
                                        sortState={requestsSort}
                                        onSort={(key) => toggleSort(setRequestsSort, key)}
                                    />
                                    <SortableTh
                                        label="Stato"
                                        sortKey="status"
                                        sortState={requestsSort}
                                        onSort={(key) => toggleSort(setRequestsSort, key)}
                                    />
                                    <SortableTh
                                        label="Origine"
                                        sortKey="origin"
                                        sortState={requestsSort}
                                        onSort={(key) => toggleSort(setRequestsSort, key)}
                                    />
                                    <SortableTh
                                        label="Review"
                                        sortKey="review"
                                        sortState={requestsSort}
                                        onSort={(key) => toggleSort(setRequestsSort, key)}
                                    />
                                    <SortableTh
                                        label="Overrides"
                                        sortKey="overrides"
                                        sortState={requestsSort}
                                        onSort={(key) => toggleSort(setRequestsSort, key)}
                                    />
                                    <th>Azioni</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRequests.map((item) => {
                                    const isBusy = busyId === item.id;
                                    const isPending = item.status === "pending";
                                    const isManual = isManualAdminAssociation(item);

                                    return (
                                        <tr
                                            key={item.id}
                                            className={isManual ? "is-admin-origin" : undefined}
                                        >
                                            <td className="admin-request-id-cell">
                                                #{item.id}
                                            </td>

                                            <td>
                                                <div className="admin-request-supplier">
                                                    <strong>
                                                        {item.supplier_name ||
                                                            `#${item.supplier_id}`}
                                                    </strong>
                                                    <span>supplier_id: {item.supplier_id}</span>
                                                </div>
                                            </td>

                                            <td>
                                                <div className="admin-request-plan">
                                                    <strong>{item.plan_name}</strong>
                                                    <span>
                                                        {item.model_name || item.model_code || "Modello non indicato"}
                                                        {" · "}
                                                        {euro(item.price_cents)}
                                                        {billingPeriodLabel(item.billing_period)}
                                                    </span>
                                                </div>
                                            </td>

                                            <td>
                                                <span
                                                    className={`admin-status-pill admin-status-pill--${item.status}`}
                                                >
                                                    {statusLabel(item.status)}
                                                </span>
                                            </td>

                                            <td>
                                                <span
                                                    className={`admin-origin-pill ${
                                                        isManual
                                                            ? "admin-origin-pill--admin"
                                                            : "admin-origin-pill--supplier"
                                                    }`}
                                                >
                                                    {requestOriginLabel(item)}
                                                </span>
                                            </td>

                                            <td>
                                                <div className="admin-request-review">
                                                    <strong>
                                                        {item.reviewed_at
                                                            ? formatShortDate(item.reviewed_at)
                                                            : "—"}
                                                    </strong>
                                                    {item.review_note ? (
                                                        <span>{item.review_note}</span>
                                                    ) : null}
                                                </div>
                                            </td>

                                            <td>
                                                <details className="admin-json-details">
                                                    <summary>JSON</summary>
                                                    <pre>{prettyJson(item.custom_overrides)}</pre>
                                                </details>
                                            </td>

                                            <td>
                                                {isPending ? (
                                                    <div className="admin-request-actions">
                                                        <button
                                                            type="button"
                                                            className="admin-btn admin-btn-sm admin-btn-primary"
                                                            disabled={isBusy}
                                                            onClick={() =>
                                                                handleReview(
                                                                    item.id,
                                                                    "approved",
                                                                )
                                                            }
                                                        >
                                                            {isBusy ? "..." : "Approva"}
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="admin-btn admin-btn-sm"
                                                            disabled={isBusy}
                                                            onClick={() =>
                                                                handleReview(
                                                                    item.id,
                                                                    "rejected",
                                                                )
                                                            }
                                                        >
                                                            {isBusy ? "..." : "Rifiuta"}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <span className="admin-action-note">
                                                        {getRequestActionText(item)}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="admin-card">
                <div className="admin-card-header admin-card-header-inline">
                    <div>
                        <h2 className="admin-section-title">
                            Associazioni fornitori · piani
                        </h2>
                        <p className="admin-subtitle">
                            Lista completa dei fornitori, piani già associati e
                            assegnazione manuale da admin.
                        </p>
                    </div>

                    <button
                        type="button"
                        className="admin-btn admin-btn-sm"
                        onClick={loadSupplierPlans}
                        disabled={loadingSupplierPlans}
                    >
                        {loadingSupplierPlans ? "Aggiorno..." : "Aggiorna"}
                    </button>
                </div>

                <div className="admin-supplier-summary-table-wrap">
                    <table className="admin-supplier-summary-table">
                        <thead>
                            <tr>
                                <th>Fornitori</th>
                                <th>Con piani</th>
                                <th>Senza piani</th>
                                <th>Approvati</th>
                                <th>In attesa</th>
                                <th>Rifiutati</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{supplierPlanStats.totalSuppliers}</td>
                                <td>{supplierPlanStats.suppliersWithPlans}</td>
                                <td>{supplierPlanStats.suppliersWithoutPlans}</td>
                                <td>{supplierPlanStats.approvedLinks}</td>
                                <td>{supplierPlanStats.pendingLinks}</td>
                                <td>{supplierPlanStats.rejectedLinks}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="admin-supplier-plans-toolbar">
                    <input
                        type="search"
                        value={supplierSearch}
                        onChange={(e) => setSupplierSearch(e.target.value)}
                        placeholder="Cerca fornitore, città o piano..."
                    />

                    <div className="admin-subtitle">
                        Fornitori mostrati:{" "}
                        <strong>{filteredSupplierRows.length}</strong> /{" "}
                        {supplierRows.length}
                    </div>
                </div>

                {loadingSupplierPlans ? (
                    <div className="admin-empty-box">
                        Caricamento associazioni fornitori...
                    </div>
                ) : supplierRows.length === 0 ? (
                    <div className="admin-empty-box">
                        Nessun fornitore trovato.
                    </div>
                ) : planOptions.length === 0 ? (
                    <div className="admin-empty-box">
                        Nessun piano attivo disponibile da associare.
                    </div>
                ) : (
                    <div className="admin-table-wrap">
                        <table className="admin-table admin-supplier-plans-table admin-supplier-plans-table--compact">
                            <thead>
                                <tr>
                                    <SortableTh
                                        label="Fornitore"
                                        sortKey="supplier"
                                        sortState={suppliersSort}
                                        onSort={(key) => toggleSort(setSuppliersSort, key)}
                                    />
                                    <SortableTh
                                        label="Località"
                                        sortKey="location"
                                        sortState={suppliersSort}
                                        onSort={(key) => toggleSort(setSuppliersSort, key)}
                                    />
                                    <SortableTh
                                        label="Piani associati"
                                        sortKey="plans"
                                        sortState={suppliersSort}
                                        onSort={(key) => toggleSort(setSuppliersSort, key)}
                                    />
                                    <th>Associa nuovo piano</th>
                                </tr>
                            </thead>

                            <tbody>
                                {sortedSupplierRows.map((supplier) => {
                                    const links = Array.isArray(
                                        supplier.subscription_plans,
                                    )
                                        ? supplier.subscription_plans
                                        : [];

                                    const linkedPlanIds = new Set(
                                        links.map((link) => String(link.plan_id)),
                                    );

                                    const selectedPlanId =
                                        selectedPlanBySupplier[supplier.id] || "";

                                    return (
                                        <tr key={supplier.id}>
                                            <td>
                                                <div className="admin-supplier-cell admin-supplier-cell--compact">
                                                    <div>
                                                        <div className="admin-supplier-name">
                                                            {supplier.nome || `#${supplier.id}`}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>

                                            <td className="admin-supplier-location-cell">
                                                <div className="admin-supplier-workzones">
                                                    {(() => {
                                                        const comuni =
                                                            getSupplierWorkZoneComuni(
                                                                supplier,
                                                            );
                                                        return comuni.length > 0
                                                            ? comuni.join(", ")
                                                            : "Nessuna zona di lavoro";
                                                    })()}
                                                </div>
                                                <div className="admin-supplier-residence-badge">
                                                    Residenza: {getSupplierLocation(supplier)}
                                                </div>
                                            </td>

                                            <td>
                                                {links.length === 0 ? (
                                                    <span className="admin-empty-inline admin-empty-inline--compact">
                                                        Nessun piano
                                                    </span>
                                                ) : (
                                                    <div className="admin-plan-inline-list">
                                                        {links.map((link) => {
                                                            const canApprove =
                                                                link.status !==
                                                                "approved";
                                                            const approveBusy =
                                                                supplierPlanBusyId ===
                                                                `assign-${supplier.id}-${link.plan_id}`;
                                                            const deleteBusy =
                                                                supplierPlanBusyId ===
                                                                `delete-${link.id}`;

                                                            return (
                                                                <div
                                                                    className={`admin-plan-inline admin-plan-inline--${link.status}`}
                                                                    key={link.id}
                                                                >
                                                                    <div className="admin-plan-inline-main">
                                                                        <strong>
                                                                            {link.plan_name ||
                                                                                `Piano #${link.plan_id}`}
                                                                        </strong>
                                                                        <span>
                                                                            {link.model_name ||
                                                                                link.model_code ||
                                                                                "Modello non indicato"}{" "}
                                                                            · {euro(link.price_cents)}{" "}
                                                                            {billingPeriodLabel(
                                                                                link.billing_period,
                                                                            )}
                                                                            {link.reviewed_at
                                                                                ? ` · review ${formatShortDate(
                                                                                      link.reviewed_at,
                                                                                  )}`
                                                                                : ""}
                                                                        </span>
                                                                    </div>

                                                                    <span
                                                                        className={`admin-status-pill admin-status-pill--${link.status}`}
                                                                    >
                                                                        {statusLabel(link.status)}
                                                                    </span>

                                                                    <div className="admin-plan-inline-actions">
                                                                        {canApprove ? (
                                                                            <button
                                                                                type="button"
                                                                                className="admin-link-btn"
                                                                                disabled={approveBusy}
                                                                                onClick={() =>
                                                                                    handleAssignPlan(
                                                                                        supplier,
                                                                                        link.plan_id,
                                                                                    )
                                                                                }
                                                                            >
                                                                                {approveBusy
                                                                                    ? "..."
                                                                                    : "Approva"}
                                                                            </button>
                                                                        ) : null}

                                                                        <button
                                                                            type="button"
                                                                            className="admin-link-btn admin-link-btn--danger"
                                                                            disabled={deleteBusy}
                                                                            onClick={() =>
                                                                                handleDeleteSupplierPlan(
                                                                                    link,
                                                                                )
                                                                            }
                                                                        >
                                                                            {deleteBusy
                                                                                ? "..."
                                                                                : "Rimuovi"}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </td>

                                            <td>
                                                <div className="admin-association-inline">
                                                    <select
                                                        value={selectedPlanId}
                                                        onChange={(e) =>
                                                            setSelectedPlanBySupplier(
                                                                (prev) => ({
                                                                    ...prev,
                                                                    [supplier.id]:
                                                                        e.target.value,
                                                                }),
                                                            )
                                                        }
                                                    >
                                                        <option value="">
                                                            Seleziona piano
                                                        </option>
                                                        {planOptions.map((plan) => {
                                                            const alreadyLinked =
                                                                linkedPlanIds.has(
                                                                    String(plan.id),
                                                                );

                                                            return (
                                                                <option
                                                                    key={plan.id}
                                                                    value={plan.id}
                                                                    disabled={alreadyLinked}
                                                                >
                                                                    {alreadyLinked
                                                                        ? `Già associato · ${getPlanOptionLabel(
                                                                              plan,
                                                                          )}`
                                                                        : getPlanOptionLabel(
                                                                              plan,
                                                                          )}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>

                                                    <button
                                                        type="button"
                                                        className="admin-btn admin-btn-sm admin-btn-primary"
                                                        disabled={
                                                            !selectedPlanId ||
                                                            supplierPlanBusyId ===
                                                                `assign-${supplier.id}-${selectedPlanId}`
                                                        }
                                                        onClick={() =>
                                                            handleAssignPlan(supplier)
                                                        }
                                                    >
                                                        {supplierPlanBusyId ===
                                                        `assign-${supplier.id}-${selectedPlanId}`
                                                            ? "..."
                                                            : "Associa"}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
